import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as contactFormApi from '@app/api/contactForm.api';
import { ContactForm, ContactFormFilters, ContactFormPaginatedResponse, UpdateContactFormDto } from '@app/types/api.types';

export interface ContactFormState {
  items: ContactForm[];
  currentItem: ContactForm | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  filters: ContactFormFilters;
  loading: boolean;
  error: string | null;
}

const initialState: ContactFormState = {
  items: [],
  currentItem: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },
  filters: {
    page: 1,
    limit: 10,
  },
  loading: false,
  error: null,
};

export const fetchContactForms = createAsyncThunk(
  'contactForm/fetchContactForms',
  async (filters: ContactFormFilters = { page: 1, limit: 10 }, { rejectWithValue }) => {
    try {
      return await contactFormApi.getContactForms(filters);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch contact forms');
    }
  }
);

export const fetchContactFormById = createAsyncThunk(
  'contactForm/fetchContactFormById',
  async (id: string, { rejectWithValue }) => {
    try {
      return await contactFormApi.getContactFormById(id);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch contact form');
    }
  }
);

export const updateContactForm = createAsyncThunk(
  'contactForm/updateContactForm',
  async ({ id, data }: { id: string; data: UpdateContactFormDto }, { rejectWithValue }) => {
    try {
      return await contactFormApi.updateContactForm(id, data);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update contact form');
    }
  }
);

export const deleteContactForm = createAsyncThunk(
  'contactForm/deleteContactForm',
  async (id: string, { rejectWithValue }) => {
    try {
      await contactFormApi.deleteContactForm(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete contact form');
    }
  }
);

const contactFormSlice = createSlice({
  name: 'contactForm',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<ContactFormFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearCurrentItem: (state) => {
      state.currentItem = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Contact Forms
    builder.addCase(fetchContactForms.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchContactForms.fulfilled, (state, action) => {
      state.loading = false;
      state.items = action.payload.data;
      state.pagination = {
        page: action.payload.page,
        limit: action.payload.limit,
        total: action.payload.total,
        totalPages: action.payload.totalPages,
        hasNext: action.payload.hasNext,
        hasPrev: action.payload.hasPrev,
      };
    });
    builder.addCase(fetchContactForms.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch Contact Form By ID
    builder.addCase(fetchContactFormById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchContactFormById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentItem = action.payload;
    });
    builder.addCase(fetchContactFormById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update Contact Form
    builder.addCase(updateContactForm.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateContactForm.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.items.findIndex((item) => item.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
      if (state.currentItem?.id === action.payload.id) {
        state.currentItem = action.payload;
      }
    });
    builder.addCase(updateContactForm.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Delete Contact Form
    builder.addCase(deleteContactForm.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteContactForm.fulfilled, (state, action) => {
      state.loading = false;
      state.items = state.items.filter((item) => item.id !== action.payload);
      if (state.currentItem?.id === action.payload) {
        state.currentItem = null;
      }
    });
    builder.addCase(deleteContactForm.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { setFilters, clearCurrentItem, clearError } = contactFormSlice.actions;
export default contactFormSlice.reducer;

